import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { buildValueToLabel, groupTokens } from "./tacticModel";
import { ZoneThumbnail } from "./ZoneThumbnail";
import "./TacticEditor.css";

// value → param type ("zone", …) for the queries that take an argument
const buildParamByValue = (queryTokens) =>
  Object.fromEntries(
    Object.entries(queryTokens)
      .filter(([, tk]) => typeof tk === "object" && tk.param)
      .map(([, tk]) => [tk.value, tk.param])
  );

const SYN_NOT = 8;

let _idCounter = 0;
const uid = () => `q_${++_idCounter}`;

const PREC = { or: 1, and: 2 };

// --- Tree ↔ flat list conversion ---

function treeToFlat(query, parentPrec = 0) {
  if (!query) return [];
  switch (query.kind) {
    case "atom":
      return [{ kind: "atom", value: query.value, arg: query.arg ?? null, negated: false, id: uid() }];
    case "not":
      if (query.operand?.kind === "atom")
        return [{ kind: "atom", value: query.operand.value, arg: query.operand.arg ?? null, negated: true, id: uid() }];
      return treeToFlat(query.operand, parentPrec);
    case "and":
    case "or": {
      const myPrec = PREC[query.kind];
      const inner  = [
        ...treeToFlat(query.left,  myPrec),
        { kind: "op", type: query.kind, id: uid() },
        ...treeToFlat(query.right, myPrec),
      ];
      if (myPrec < parentPrec) {
        const gid = uid();
        return [
          { kind: "paren", side: "open",  groupId: gid, id: uid() },
          ...inner,
          { kind: "paren", side: "close", groupId: gid, id: uid() },
        ];
      }
      return inner;
    }
    default:
      return [];
  }
}

function atomToNode(item) {
  const atom = item.arg != null
    ? { kind: "atom", value: item.value, arg: item.arg }
    : { kind: "atom", value: item.value };
  return item.negated ? { kind: "not", operand: atom } : atom;
}

// Recursive descent parser — AND binds tighter than OR
function flatToTree(items) {
  let pos = 0;
  const peek    = () => items[pos];
  const consume = () => items[pos++];

  function parseExpr() { return parseOr(); }

  function parseOr() {
    let left = parseAnd();
    while (peek()?.kind === "op" && peek()?.type === "or") {
      consume();
      const right = parseAnd();
      if (!right) break;
      left = { kind: "or", left, right };
    }
    return left;
  }

  function parseAnd() {
    let left = parsePrimary();
    while (peek()?.kind === "op" && peek()?.type === "and") {
      consume();
      const right = parsePrimary();
      if (!right) break;
      left = { kind: "and", left, right };
    }
    return left;
  }

  function parsePrimary() {
    const item = peek();
    if (!item) return null;
    if (item.kind === "paren" && item.side === "open") {
      consume();
      const expr = parseExpr();
      if (peek()?.kind === "paren" && peek()?.side === "close") consume();
      return expr;
    }
    if (item.kind === "atom") { consume(); return atomToNode(item); }
    consume();
    return parsePrimary();
  }

  return parseExpr();
}

// --- Generic grouped dropdown (queries or zones) ---

// renderIcon(value) -> optional small element shown before the label. This is
// the extensible hook for token icons: zones pass a mini-field thumbnail;
// other token families can be added little by little (fallback = text only).
function GroupedPicker({ tokenMap, onPick, onClose, renderIcon }) {
  const ref    = useRef(null);
  const groups = groupTokens(tokenMap);
  const [openGroup, setOpenGroup] = useState(Object.keys(groups)[0]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  return (
    <div ref={ref} className="action-picker atom-picker">
      {Object.entries(groups).map(([group, its]) => (
        <div key={group} className="action-picker-group">
          <button
            className={`action-picker-group-label ${openGroup === group ? "open" : ""}`}
            onClick={() => setOpenGroup(openGroup === group ? null : group)}
          >
            <span>{group}</span>
            <span className="action-picker-arrow">{openGroup === group ? "▾" : "▸"}</span>
          </button>
          {openGroup === group && its.map(({ label, value }) => (
            <button key={value} className="action-picker-option action-picker-option--icon" onClick={() => onPick(value)}>
              {renderIcon && renderIcon(value)}
              <span>{label}</span>
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

// icon hook for the zone picker: a mini-field with the zone highlighted
const renderZoneIcon = (value) => <ZoneThumbnail value={value} size={26} />;

// Query picker with a two-step flow for parametrized queries: pick a query;
// if it takes a zone, a second grouped picker asks for the zone, then
// onSelect(queryValue, zoneValue). Plain queries -> onSelect(value, null).
// Dismissing during the zone step cancels the whole selection (deliberate:
// a zone query is never added without its zone).
function AtomPicker({ queryTokens, zones, onSelect, onClose }) {
  const [pendingQuery, setPendingQuery] = useState(null);
  const paramByValue = useMemo(() => buildParamByValue(queryTokens), [queryTokens]);

  if (pendingQuery != null) {
    return (
      <GroupedPicker
        key="zones"
        tokenMap={zones ?? {}}
        renderIcon={renderZoneIcon}
        onPick={(zoneVal) => onSelect(pendingQuery, zoneVal)}
        onClose={onClose}
      />
    );
  }
  return (
    <GroupedPicker
      key="queries"
      tokenMap={queryTokens}
      onPick={(qVal) => {
        if (paramByValue[qVal]) setPendingQuery(qVal); // -> zone step
        else onSelect(qVal, null);
      }}
      onClose={onClose}
    />
  );
}

// ⊕ between chips — inserts a new atom at that position
function AtomInsertBtn({ onInsert }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <span ref={ref} className="paren-insert-wrapper">
      <button className="insert-btn" onClick={() => setOpen(true)} title="Insert condition here">⊕</button>
      {open && (
        <AtomPicker
          queryTokens={onInsert.queryTokens}
          zones={onInsert.zones}
          onSelect={(v, arg) => { onInsert(v, arg); setOpen(false); }}
          onClose={() => setOpen(false)}
        />
      )}
    </span>
  );
}

// --- Modal ---

/**
 * @param {{ query: object, queryTokens: object, onSave: (q: object) => void, onClose: () => void }} props
 */
export function QueryBuilderModal({ query, queryTokens, syntaxTokens, zones, onSave, onClose }) {
  const { t } = useTranslation();
  const [items, setItems]         = useState(() => treeToFlat(query));
  const [pickerFor, setPickerFor] = useState(null); // atom id, "new", or "new-group"
  const [zonePickerFor, setZonePickerFor] = useState(null); // atom id whose ZONE picker is open

  const byValue       = buildValueToLabel(queryTokens);
  const zoneByValue   = buildValueToLabel(zones ?? {});
  const syntaxByValue = buildValueToLabel(syntaxTokens ?? {});
  const kwNot = syntaxByValue[SYN_NOT] ?? "NOT";
  const kwAnd = syntaxByValue[6] ?? "AND";
  const kwOr  = syntaxByValue[7] ?? "OR";

  const updateItem = (id, changes) =>
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...changes } : item)));

  const removeAtom = (id) => {
    setItems((prev) => {
      const idx  = prev.findIndex((i) => i.id === id);
      if (idx === -1) return prev;
      const next = [...prev];
      if (idx > 0 && next[idx - 1].kind === "op") next.splice(idx - 1, 2);
      else if (next[idx + 1]?.kind === "op")       next.splice(idx, 2);
      else                                          next.splice(idx, 1);
      return next;
    });
  };

  // removes the whole () group: open paren + content + close paren
  const removeGroup = (closeId) => {
    setItems((prev) => {
      const closeIdx = prev.findIndex((i) => i.id === closeId);
      if (closeIdx === -1) return prev;
      const groupId  = prev[closeIdx].groupId;
      const openIdx  = prev.findIndex((i) => i.kind === "paren" && i.groupId === groupId && i.side === "open");
      if (openIdx === -1) return prev;
      const next = [...prev];
      // also remove adjacent op outside the group
      const start = openIdx > 0 && next[openIdx - 1].kind === "op" ? openIdx - 1 : openIdx;
      next.splice(start, closeIdx - start + 1 + (start < openIdx ? 1 : 0));
      return next;
    });
  };

  // inserts a new atom at position afterIndex, adding AND op on the correct side
  const insertAtomAt = (afterIndex, value, arg = null) => {
    setItems((prev) => {
      const newAtom = { kind: "atom", value, arg, negated: false, id: uid() };
      if (prev.length === 0) return [newAtom];
      const next     = [...prev];
      const newOp    = { kind: "op", type: "and", id: uid() };
      const prevItem = next[afterIndex];
      if (!prevItem || prevItem.kind === "atom" || (prevItem.kind === "paren" && prevItem.side === "close")) {
        next.splice(afterIndex + 1, 0, newOp, newAtom);
      } else {
        next.splice(afterIndex + 1, 0, newAtom, newOp);
      }
      return next;
    });
  };

  const addGroup = (value, arg = null) => {
    const gid  = uid();
    const atom = { kind: "atom", value, arg, negated: false, id: uid() };
    setItems((prev) => {
      const group = [
        { kind: "paren", side: "open",  groupId: gid, id: uid() },
        atom,
        { kind: "paren", side: "close", groupId: gid, id: uid() },
      ];
      return prev.length === 0
        ? group
        : [...prev, { kind: "op", type: "and", id: uid() }, ...group];
    });
    setPickerFor(null);
  };

  const swapAtom = (id, value, arg = null) => {
    updateItem(id, { value, arg });
    setPickerFor(null);
  };

  const [error, setError] = useState(null);

  const handleSave = () => {
    const atoms = items.filter((i) => i.kind === "atom");
    if (atoms.length === 0) {
      setError("empty_condition");
      return;
    }
    const tree = flatToTree(items);
    if (tree) onSave(tree);
    else onClose();
  };

  // build an insertAtomAt callback that also carries queryTokens for AtomInsertBtn
  const makeInserter = (afterIndex) => {
    const fn = (value, arg) => insertAtomAt(afterIndex, value, arg);
    fn.queryTokens = queryTokens;
    fn.zones = zones;
    return fn;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>

        <div className="modal-header">
          <span className="modal-title">{t("tacticEditor.editCondition")}</span>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="query-builder-row">
          {items.length === 0 && (
            <span className="query-builder-empty">{t("tacticEditor.noConditions")}</span>
          )}

          {/* single ⊕ when empty, inserting the first atom */}
          {items.length === 0 && <AtomInsertBtn onInsert={makeInserter(-1)} />}

          {items.map((item, i) => (
            <React.Fragment key={item.id}>

              {/* ⊕ before first item and between items */}
              {(i === 0 || true) && i === 0
                ? null
                : <AtomInsertBtn onInsert={makeInserter(i - 1)} />
              }

              {item.kind === "paren" && item.side === "open" && (
                <span className="query-paren-chip">(</span>
              )}

              {item.kind === "paren" && item.side === "close" && (
                <span className="query-paren-chip">
                  )
                  <button
                    className="delete-btn paren-delete"
                    onClick={() => removeGroup(item.id)}
                    title="Remove group"
                  >✕</button>
                </span>
              )}

              {item.kind === "op" && (
                <button
                  className="query-op query-op-toggle"
                  onClick={() => updateItem(item.id, { type: item.type === "and" ? "or" : "and" })}
                  title="Tap to toggle AND / OR"
                >
                  {item.type === "and" ? kwAnd : kwOr}
                </button>
              )}

              {item.kind === "atom" && (
                <span className="query-atom-wrapper">
                  <button
                    className={`query-not-toggle ${item.negated ? "active" : ""}`}
                    onClick={() => updateItem(item.id, { negated: !item.negated })}
                    title="Toggle NOT"
                  >
                    {kwNot}
                  </button>
                  <span
                    className="query-chip selectable"
                    onClick={() => setPickerFor(item.id)}
                    title="Tap to change condition"
                  >
                    {byValue[item.value] ?? item.value}
                  </span>
                  {item.arg != null && (
                    <span
                      className="query-chip-arg selectable"
                      onClick={() => setZonePickerFor(item.id)}
                      title="Tap to change zone"
                    >
                      ({zoneByValue[item.arg] ?? item.arg})
                    </span>
                  )}
                  {pickerFor === item.id && (
                    <AtomPicker
                      queryTokens={queryTokens}
                      zones={zones}
                      onSelect={(v, arg) => swapAtom(item.id, v, arg)}
                      onClose={() => setPickerFor(null)}
                    />
                  )}
                  {zonePickerFor === item.id && (
                    <GroupedPicker
                      tokenMap={zones ?? {}}
                      renderIcon={renderZoneIcon}
                      onPick={(zoneVal) => { updateItem(item.id, { arg: zoneVal }); setZonePickerFor(null); }}
                      onClose={() => setZonePickerFor(null)}
                    />
                  )}
                  <button className="delete-btn" onClick={() => removeAtom(item.id)} title="Remove">✕</button>
                </span>
              )}

            </React.Fragment>
          ))}

          {/* ⊕ after last item */}
          {items.length > 0 && <AtomInsertBtn onInsert={makeInserter(items.length - 1)} />}

        </div>

        {error && <div className="balance-error-msg">{t(`tacticEditor.${error}`)}</div>}

        <div className="modal-footer">
          <div className="modal-add-btns">
            <div style={{ position: "relative" }}>
              <button className="add-statement-btn" onClick={() => setPickerFor("new-group")}>
                {t("tacticEditor.addGroup")}
              </button>
              {pickerFor === "new-group" && (
                <AtomPicker
                  queryTokens={queryTokens}
                  zones={zones}
                  onSelect={addGroup}
                  onClose={() => setPickerFor(null)}
                />
              )}
            </div>
          </div>
          <div className="modal-actions">
            <button className="modal-cancel-btn" onClick={onClose}>{t("cancel")}</button>
            <button className="modal-save-btn" onClick={handleSave}>{t("save")}</button>
          </div>
        </div>

      </div>
    </div>
  );
}
